import React, { useEffect, useState } from "react";
import axiosInstance from '../../../../axiosInstance';
import { API_URL } from "../../../../config";
import { Form, Alert } from "react-bootstrap";
import { Slider } from "@mui/material";
import { Info } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Helmet } from 'react-helmet-async';

function SectionCandicateBasicInfo() {
    const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const [location, setLocation] = useState('');
    const [experience, setExperience] = useState('');
    const [jobType, setJobType] = useState('');
    const [status, setStatus] = useState('active');
    const [totalAppliedJobs, setTotalAppliedJobs] = useState(0);
    const [remoteOnly, setRemoteOnly] = useState(false);
    const [cvFile, setCvFile] = useState(null);
    const [sendEmails, setSendEmails] = useState(false);
    const [autoFill, setAutoFill] = useState(false);
    const [cvAdded, setCvAdded] = useState(false);
    const [value, setValue] = useState(0);
    const [templates, setTemplates] = useState([]);
    const [ownTemplates, setOwnTemplates] = useState([]);
    const [emailTitle, setEmailTitle] = useState("");
    const [emailSubject, setEmailSubject] = useState("");
    const [emailBody, setEmailBody] = useState("");
    const [selectedLang, setSelectedLang] = useState("english");
    const [subscriptionValid, setSubscriptionValid] = useState(null);
    const [errors, setErrors] = useState({});
    const [totalJobLoops, setTotalJobLoops] = useState(0);
    const [canSaveTemplate, setCanSaveTemplate] = useState(false);
    const [loading, setLoading] = useState(true);

    const experienceOptions = [
        { value: "Junior", label: t("Junior") },
        { value: "Medior", label: t("Medior") },
        { value: "Senior", label: t("Senior") },
        { value: "All", label: t("All") },
    ];

    const jobtypeOptions = [
        { value: "Full time", label: t("Full time") },
        { value: "Contract", label: t("Contract") },
        { value: "Part time", label: t("Part time") },
        { value: "Temporary", label: t("Temporary") },
        { value: "Internship", label: t("Internship") }
    ];

    const validateForm = () => {
        const newErrors = {};

        // Job Loop fields
        if (!title.trim()) newErrors.title = "Job Title is required";
        if (!location.trim()) newErrors.location = "Location is required";
        if (!experience) newErrors.experience = "Experience is required";
        if (!jobType) newErrors.jobType = "Job Type is required";
        if (!cvFile) newErrors.cvFile = "CV is required";
        if (typeof remoteOnly === "undefined") newErrors.remoteOnly = "Remote Only setting is required";
        if (!status) newErrors.status = "Status is required";

        // Loop settings
        if (!sendEmails && !autoFill) {
            newErrors.autoSettings = "At least one of Auto Send Emails or Auto Fill Application must be enabled";
        }

        setErrors(newErrors);
        return newErrors;
    };

    const [selectedTemplate, setSelectedTemplate] = useState({
        title: '',
        subject: '',
        body: '',
        status: 'active'
    });

    useEffect(() => {
        const lastTemplate = localStorage.getItem('lastSelectedTemplate');
        if (lastTemplate) {
            const parsed = JSON.parse(lastTemplate);
            setSelectedTemplate(parsed);
            setEmailTitle(parsed.title);
            setEmailSubject(parsed.subject);
            setEmailBody(parsed.body);
        }

        const fetchTemplates = async () => {
            try {
                const response = await axiosInstance.get(`${API_URL}/admin/get-all-emailTemplate`);
                const all = response.data.data;

                const filtered = all.filter(t => t.lang === selectedLang);
                const own = filtered.filter(t => t.user_id);
                const global = filtered.filter(t => !t.user_id);

                setOwnTemplates(own);
                setTemplates(global);
            } catch (error) {
                console.error("Error fetching templates:", error.response?.data || error.message);
            }
        };

        const fetchDashboardDetails = async () => {
            try {
                const res = await axiosInstance.get(`${API_URL}/Dashboards`);
                const data = res.data.data;

                setTotalJobLoops(data.totalJobLoops || 0);

                if (data.user?.expiry_date) {
                    const expiry = new Date(data.user.expiry_date);
                    const today = new Date();
                    setSubscriptionValid(expiry > today);
                } else {
                    setSubscriptionValid(false);
                }

            } catch (err) {
                console.error("Failed to fetch dashboard details", err);
            }
            setLoading(false);
        };
        fetchDashboardDetails();
        fetchTemplates();
    }, [selectedLang]);

    const handleSelectTemplate = (template) => {
        setSelectedTemplate(template);
        setEmailTitle(template.title);
        setEmailSubject(template.subject);
        setEmailBody(template.body);
        localStorage.setItem('lastSelectedTemplate', JSON.stringify(template));
    };

    const handleCreateTemplate = async () => {
        try {
            const response = await axiosInstance.post(`${API_URL}/addLoopEmail`, selectedTemplate);
            toast.success("Template created successfully!");

            const newTemplate = { ...selectedTemplate, id: response.data.data.id };

            // Add it only if not already in the list
            setOwnTemplates(prev => {
                const exists = prev.some(t => t.id === newTemplate.id);
                return exists ? prev : [newTemplate, ...prev];
            });

            // Just select it (don’t duplicate in state)
            setSelectedTemplate(newTemplate);

        } catch (error) {
            console.error("Create template error:", error.response?.data || error.message);
            toast.error("Failed to create template.");
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCvFile(file);
            setCvAdded(true);
        }
    };

    const handleSaveLoopSettings = async (event) => {
        event.preventDefault();

        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            const firstError = Object.values(validationErrors)[0];
            toast.error(firstError);
            return;
        }

        try {
            const formData = new FormData();
            if (cvFile) {
                formData.append("cv", cvFile);
            }

            const settingsPayload = {
                title,
                location,
                is_remote: remoteOnly,
                experience,
                job_type: jobType,
                status,
                total_applied_jobs: totalAppliedJobs || 0,
                auto_send_emails: sendEmails,
                auto_fill_application: autoFill,
                email_template:
                    selectedTemplate?.title && selectedTemplate?.subject && selectedTemplate?.body
                        ? {
                            title: selectedTemplate.title,
                            subject: selectedTemplate.subject,
                            body: selectedTemplate.body,
                            status: selectedTemplate.status || "active",
                        }
                        : undefined,
            };

            formData.append("data", JSON.stringify(settingsPayload));

            await axiosInstance.post(`${API_URL}/addJobLoop`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            toast.success("Job loop saved successfully!");

            setTimeout(() => {
                window.location.reload();
            }, 800);

            // reset form after success
            setTitle("");
            setLocation("");
            setExperience("");
            setJobType("");
            setStatus("active");
            setTotalAppliedJobs(0);
            setRemoteOnly(false);
            setCvFile(null);
            setCvAdded(false);
            setSendEmails(false);
            setAutoFill(false);
            setSelectedTemplate({ title: "", subject: "", body: "", status: "active" });
        } catch (error) {
            console.error("Save settings error:", error.response?.data || error.message);
            if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error("Failed to save settings.");
            }
        }
    };

    // const handleChange = (event, newValue) => setValue(newValue);
    // const getLabel = () => {
    //     if (value <= 33) return `${t("Low match with your preferences")}`;
    //     if (value <= 66) return `${t("Medium match with your preferences")}`;
    //     return `${t("High match with your preferences")}`;
    // };

    if (!subscriptionValid && totalJobLoops >= 1) {
        return (
            <div className="alert alert-warning">
                {t("You have already created 1 job loops with your free plan. Please upgrade your plan to create more.")}{" "}
                <a href="/pricing">{t("Go to Pricing")}</a>
            </div>
        );
    }
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
                <div className="spinner-border site-text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }
    // if (!subscriptionValid) {
    //     return (
    //         <div className="alert alert-warning">
    //             {t("You must have an active subscription to access this feature.")}{" "}
    //             <a href="/pricing">{t("Go to Pricing")}</a>
    //         </div>
    //     );
    // }

    return (
        <>
            <Helmet>
                <title>Add Loop | Job Contact</title>
                <meta name="description" content="Welcome to the home page of MyApp." />
            </Helmet>
            <form onSubmit={handleSaveLoopSettings}>
                <div className="panel panel-default">
                    <div className="panel-heading wt-panel-heading p-a20">
                        <h4 className="panel-tittle m-a0">{t("Complete your desired job info and location")}</h4>
                    </div>
                    <div className="panel-body wt-panel-body p-a20 m-b30 ">
                        <div className="row">
                            <div className="col-xl-12 col-lg-12 col-md-12">
                                <div className="form-group city-outer-bx has-feedback">
                                    <label>{t("Job Titles")}</label>
                                    <div className="ls-inputicon-box">
                                        <input
                                            type="text"
                                            name="title"
                                            className="form-control"
                                            placeholder={`${t("Software Engineer, Frontend Developer, etc.")}`}
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                            style={{
                                                borderRadius: "4px",
                                                borderColor: "#ccc",
                                                minHeight: "40px",
                                            }} />
                                        <i className="fs-input-icon fa fa-briefcase" />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {t("This job title will be used to search for jobs around the web")}
                                    </p>
                                </div>
                            </div>
                            <div className="col-xl-12 col-lg-12 col-md-12">
                                <div className="form-group city-outer-bx has-feedback">
                                    <label>{t("Job Location")}</label>
                                    <div className="ls-inputicon-box">
                                        <input
                                            type="text"
                                            name="location"
                                            value={location}
                                            onChange={(e) => setLocation(e.target.value)}
                                            className="form-control"
                                            placeholder={`${t("Enter Location.")}`}
                                            style={{
                                                borderRadius: "4px",
                                                borderColor: "#ccc",
                                                minHeight: "40px",
                                            }} />
                                        <i className="fs-input-icon fas fa-map-marker-alt" />
                                    </div>
                                </div>
                            </div>
                            <div className="col-xl-12 col-lg-12 col-md-12">
                                <div className="form-check mb-3">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="remoteOnly"
                                        checked={remoteOnly}
                                        onChange={() => setRemoteOnly(!remoteOnly)} />
                                    <label className="form-check-label" htmlFor="remoteOnly">
                                        {t("Search only for remote jobs")}
                                    </label>
                                </div>
                            </div>
                            <div className="col-xl-6 col-lg-6 col-md-12 mt-5">
                                <div className="form-group">
                                    <label>{t("Experience")}</label>
                                    <div className="ls-inputicon-box">
                                        <select
                                            name="experience"
                                            value={experience}
                                            onChange={(e) => setExperience(e.target.value)}
                                            className="form-control"
                                            style={{
                                                borderRadius: "4px",
                                                borderColor: "#ccc",
                                                minHeight: "40px",
                                            }}
                                        >
                                            <option value="">{t("Select Experience")}</option>
                                            {experienceOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        <i className="fs-input-icon fa fa-clock" />
                                    </div>
                                </div>
                            </div>
                            <div className="col-xl-6 col-lg-6 col-md-12 mt-5">
                                <div className="form-group">
                                    <label>{t("Job Type")}</label>
                                    <div className="ls-inputicon-box">
                                        <select
                                            name="jobtype"
                                            value={jobType}
                                            onChange={(e) => setJobType(e.target.value)}
                                            className="form-control"
                                            style={{
                                                borderRadius: "4px",
                                                borderColor: "#ccc",
                                                minHeight: "40px",
                                            }}
                                        >
                                            <option value="">{t("Select Job Type")}</option>
                                            {jobtypeOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        <i className="fs-input-icon fa fa-tag" />
                                    </div>
                                </div>
                            </div>
                            <div className="col-xl-12 col-lg-12 col-md-12">
                                <div className="cv-upload-section mt-5">
                                    <label className="font-semibold text-lg">{t("Upload your CV (résumé)")}</label>
                                    <div className="mt-2 mb-3 text-gray-400 text-sm">
                                        <span className="uppercase">{cvFile?.name || t("No CVs Uploaded Yet")}</span>
                                    </div>
                                    <div className="text-sm text-gray-700 leading-6">
                                        <p><strong>› {t("Automate your applications")}</strong> {t("by having your CV automatically attached to emails sent to companies.")}</p>
                                        <p><strong>› {t("Apply directly")}</strong> {t("to online forms effortlessly.")}</p>
                                        <p><strong>› {t("Get better job matches")}</strong> {t("tailored to your experience and skills.")}</p>
                                    </div>
                                    <div
                                        className="d-flex flex-column align-items-center justify-content-center"
                                        style={{ height: "100px", width: '100%', border: '2px dashed black', borderRadius: '6px' }}
                                    >
                                        <p>{t("Drop your file here, or upload one from your device.")}</p>
                                        <p style={{ cursor: 'pointer', color: 'red' }} onClick={() => document.getElementById("fileUpload").click()}>
                                            {t("SELECT A PDF OR WORD FILE")}
                                        </p>
                                        <input
                                            type="file"
                                            id="fileUpload"
                                            accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                            onChange={handleFileChange}
                                            className="d-none" />
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '100px', marginTop: '15px' }}>
                                <div className="col-xl-3 col-lg-6 col-md-12 mt-4">
                                    <h5 className="panel-tittle m-a0">{t("Select or create a unique email template")} :-</h5>
                                    <div className="form-group mt-3">
                                        <label>{t("Select Language")}</label>
                                        <div className="d-flex gap-2 mt-3">
                                            <button
                                                type="button"
                                                className={`btn ${selectedLang === 'english' ? 'btn-danger' : 'btn-outline-secondary'}`}
                                                onClick={() => setSelectedLang("english")}
                                            >
                                                English
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn ${selectedLang === 'français' ? 'btn-danger' : 'btn-outline-secondary'}`}
                                                onClick={() => setSelectedLang("français")}
                                            >
                                                Français
                                            </button>
                                        </div>
                                    </div>
                                    <span>{t("Popular templates")}</span>
                                    <div
                                        style={{
                                            fontFamily: 'sans-serif',
                                            padding: '20px',
                                            overflowY: 'auto',
                                            maxHeight: '400px',
                                            width: '300px',
                                        }}
                                    >
                                        {templates.map((item, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    backgroundColor: '#f0f0f0',
                                                    padding: '15px',
                                                    marginBottom: '10px',
                                                    borderRadius: '5px',
                                                }}
                                            >
                                                <p style={{ margin: '0', fontSize: '18px' }}>{item.title}</p>
                                                <button
                                                    type="button"
                                                    style={{
                                                        backgroundColor: 'transparent',
                                                        border: 'none',
                                                        color: 'blue',
                                                        fontSize: '16px',
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={() => handleSelectTemplate(item)}
                                                >
                                                    {t("SELECT")}
                                                </button>
                                            </div>
                                        ))}
                                        <div style={{ height: '10px', backgroundColor: '#888', marginTop: '20px', borderRadius: '5px' }}></div>
                                    </div>
                                    <div className="mt-4">
                                        <div style={{ fontFamily: 'sans-serif', padding: '20px' }}>
                                            <h4 style={{ margin: '0 0 20px 0' }}>{t("My templates")}</h4>
                                            <div
                                                style={{
                                                    width: '250px',
                                                    maxHeight: '400px',
                                                    overflowY: 'auto',
                                                    paddingRight: '10px',
                                                }}
                                            >
                                                {ownTemplates.map((item, index) => (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            backgroundColor: '#f0f0f0',
                                                            padding: '15px',
                                                            borderRadius: '5px',
                                                            marginBottom: '20px',
                                                        }}
                                                    >
                                                        <p style={{ margin: '0', fontSize: '18px' }}>{item.title}</p>
                                                        <button
                                                            type="button"
                                                            style={{
                                                                backgroundColor: 'transparent',
                                                                border: 'none',
                                                                color: 'blue',
                                                                fontSize: '16px',
                                                                cursor: 'pointer',
                                                            }}
                                                            onClick={() => handleSelectTemplate(item)}
                                                        >
                                                            {t("SELECT")}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedTemplate({ title: '', subject: '', body: '', status: 'active' });
                                                    setCanSaveTemplate(true);
                                                }}
                                                style={{
                                                    marginTop: '20px',
                                                    backgroundColor: '#e0e0e0',
                                                    border: 'none',
                                                    padding: '10px 20px',
                                                    borderRadius: '20px',
                                                    fontSize: '16px',
                                                    cursor: 'pointer',
                                                    width: '250px',
                                                }}
                                            >
                                                {t("CREATE YOUR TEMPLATE")}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ flex: 1, minWidth: '300px', fontFamily: 'sans-serif', padding: '20px' }}>
                                    <div style={{ marginBottom: '20px' }}>
                                        <label htmlFor="templateName" style={{ display: 'block', marginBottom: '5px' }}>
                                            {t("Email template name")}
                                        </label>
                                        <input
                                            type="text"
                                            id="templateName"
                                            value={selectedTemplate?.title || ''}
                                            onChange={(e) => setSelectedTemplate({ ...selectedTemplate, title: e.target.value })}
                                            style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }} />
                                    </div>
                                    <div style={{ marginBottom: '20px' }}>
                                        <label htmlFor="emailSubject" style={{ display: 'block', marginBottom: '5px' }}>
                                            {t("Email subject")}
                                        </label>
                                        <input
                                            type="text"
                                            id="emailSubject"
                                            value={selectedTemplate?.subject || ''}
                                            onChange={(e) => setSelectedTemplate({ ...selectedTemplate, subject: e.target.value })}
                                            style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }} />
                                    </div>
                                    <div style={{ marginBottom: '20px' }}>
                                        <label htmlFor="emailBody" style={{ display: 'block' }}>
                                            {t("Email body")}
                                        </label>
                                        <textarea
                                            id="emailBody"
                                            rows="8"
                                            value={selectedTemplate?.body || ''}
                                            onChange={(e) => setSelectedTemplate({ ...selectedTemplate, body: e.target.value })}
                                            style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ccc', resize: 'vertical' }} />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleCreateTemplate}
                                        disabled={!canSaveTemplate}
                                        style={{
                                            color: 'white',
                                            backgroundColor: canSaveTemplate ? 'green' : 'gray',
                                            borderRadius: '10px',
                                            padding: '5px',
                                            cursor: canSaveTemplate ? 'pointer' : 'not-allowed',
                                        }}
                                    >
                                        {t("SAVE")}
                                    </button>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                                        <span style={{ color: 'red', marginRight: '5px' }}>!</span>
                                        <span style={{ fontSize: '14px' }}>
                                            {t("NOTE: We will NOT attach your CV to this email.")}
                                        </span>
                                    </div>
                                    <Alert variant="danger">
                                        <span>
                                            ❗ {t("Placeholders Instructions :")}
                                            <br />
                                            {'{{JOB_TITLE}}'} - {t("Enter the job title you are applying for.")}
                                            <br />
                                            {'{{INTERVIEWER_NAME}}'} - {t("Enter the name of the interviewer.")}
                                            <br />
                                            {'{{COMPANY_NAME}}'} - {t("Enter the company’s name.")}
                                            <br />
                                            {'{{SKILL_SET}}'} - {t("Enter the key skills relevant to the job.")}
                                            <br />
                                            {'{{USER_FIRSTNAME}} {{USER_LASTNAME}}'} - {t("Your first and last name will auto-fill.")}
                                            <br /><br />
                                            {t("Note: These fields are placeholders. They will be replaced with actual values automatically when the email is sent via automation.")}
                                        </span>
                                    </Alert>
                                </div>
                            </div>
                            <div className="mt-4">
                                <h4 className="mb-4">{t("More Settings")}</h4>
                                <div className="mb-4">
                                    <Form.Label className="fw-bold">{t("Automatically send emails")}</Form.Label>
                                    <p className="text-muted">
                                        {t("Enable this to let our platform instantly send emails to matching companies,streamlining your job hunt. Prefer reviewing emails first? Simply turn this off to personalise messages, ensuring alignment with your preferences before dispatch.")}
                                    </p>
                                    <Form.Check
                                        type="switch"
                                        checked={sendEmails}
                                        onChange={() => setSendEmails(!sendEmails)} />
                                </div>
                                {/* Auto-fill Application Forms */}
                                <div className="mb-4">
                                    <Form.Label className="fw-bold">{t("Auto-fill Application Forms")}</Form.Label>
                                    <p className="text-muted">
                                        {t("Enable this to allow our platform to automatically submit job application forms")}
                                        {t("on your behalf.")}{" "}
                                        {t("Check")}
                                        {t("an example application form that we usually fill for you.")}
                                    </p>
                                    {!cvAdded && (
                                        <Alert variant="danger" className="py-1">
                                            ❗ {t("You cannot enable this because you didn’t add a CV.")}
                                        </Alert>
                                    )}
                                    <Form.Check
                                        type="switch"
                                        checked={autoFill}
                                        onChange={() => setAutoFill(!autoFill)}
                                        disabled={!cvAdded} />
                                </div>
                                {/* <div>
                                    <Form.Label className="fw-bold">{t("Do you want to exclude some companies ?")}</Form.Label>
                                    <p className="text-muted">{t("Select companies that will not be part of your search")}</p>
                                    <Form.Control
                                        type="text"
                                        placeholder="Company"
                                        value={excludeCompany}
                                        onChange={(e) => setExcludeCompany(e.target.value)} />
                                    <p className="text-muted mt-2">
                                        {t("You don’t have any excluded companies selected for this Loop")}
                                    </p>
                                </div> */}
                                {/* <div className="col-xl-12 col-lg-12 col-md-12">
                                    <div className="form-group city-outer-bx has-feedback">
                                        <label className="fw-bold" style={{ marginBottom: '10px' }}>Cover Letter Content</label>
                                        <textarea
                                            name="cover_letter_content"
                                            className="form-control"
                                            placeholder="Write your cover letter here..."
                                            rows="8"
                                            style={{
                                                borderRadius: "4px",
                                                borderColor: "#ccc",
                                                minHeight: "120px",
                                                resize: "vertical",
                                            }}
                                            value={coverLetter}
                                            onChange={(e) => setCoverLetter(e.target.value)}
                                        ></textarea>
                                        <p className="text-xs text-gray-500 mt-1">
                                            This content will be sent when applying for jobs with a cover letter.
                                        </p>
                                    </div>
                                </div> */}
                            </div>

                            <div className="col-lg-12 col-md-12">
                                <div className="text-left">
                                    <button type="submit" className="site-button">{t("Save Changes")}</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </>
    );
}

export default SectionCandicateBasicInfo;